// Client for the gazar backend. The session is the same `token` cookie the rest
// of gazar.dev uses: it is httpOnly and scoped to `.gazar.dev`, so signing in on
// coding.gazar.dev is the same session as gazar.dev and there is no token for
// page JavaScript (or an XSS payload) to read. Every request therefore has to
// send credentials explicitly.

const BASE = import.meta.env.VITE_API_URL ?? "https://api.gazar.dev";

export type User = { id: string; email: string; name: string | null };

export type Progress = {
  drillId: string;
  pattern: string;
  attempts: number;
  passes: number;
  streak: number;
  ease: number;
  intervalDays: number;
  dueAt: string;
  bestScore: number;
  bestTimeMs: number | null;
  lastGrade: number | null;
  lastAttemptAt: string | null;
};

export type Stats = {
  dayStreak: number;
  totalScore: number;
  totalAttempts: number;
  todayAttempts: number;
  todayPasses: number;
  todayScore: number;
  dueNow: number;
};

export type AttemptFacts = {
  drillId: string;
  pattern: string;
  passed: boolean;
  elapsedMs: number;
  timeBudgetMs: number;
  runs: number;
  revealed: boolean;
  basePoints: number;
  /** Rungs of the hint ladder taken, 0 to 3. The server prices it. */
  hintsUsed: number;
};

/** Every term that produced a score, so the number can be explained. */
export type ScoreBreakdown = {
  base: number;
  speed: number;
  runs: number;
  hints: number;
  streak: number;
  total: number;
};

export type AttemptResult = {
  grade: 0 | 1 | 2 | 3;
  score: number;
  breakdown: ScoreBreakdown;
  ahead: boolean;
  progress: Progress;
  stats: Stats;
};

/** One recorded attempt: how long, how many runs, what happened, what it scored. */
export type Attempt = {
  id: string;
  drillId: string;
  pattern: string;
  passed: boolean;
  grade: 0 | 1 | 2 | 3;
  score: number;
  elapsedMs: number;
  runs: number;
  revealed: boolean;
  ahead: boolean;
  hintsUsed: number;
  createdAt: string;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Declare a JSON body only when there actually is one. Fastify rejects a
  // request that advertises `application/json` and then sends nothing with
  // "Body cannot be empty when content-type is set to 'application/json'",
  // which is what broke sign-out: POST /api/auth/logout takes no body.
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (init.body != null) headers["Content-Type"] = "application/json";

  const response = await fetch(`${BASE}${path}`, { ...init, credentials: "include", headers });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(response.status, body.message ?? `Request failed (${response.status})`);
  }
  // 204 and other empty successes have nothing to parse. None of the current
  // endpoints answer that way, but returning null beats throwing a JSON error
  // if one ever does.
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return null as T;
  }
  return response.json() as Promise<T>;
}

/**
 * Signed the human way: positive is east of UTC, so London in summer is +60.
 * The server buckets the daily streak with it, which is why a 00:30 session
 * counts towards that night rather than the next morning.
 */
export const utcOffsetMinutes = () => -new Date().getTimezoneOffset();

export type DrillProfile = {
  handle: string | null;
  onLeaderboard: boolean;
  /** Track ids the user practises. Never empty; the server substitutes the default.  */
  tracks: string[];
};

export type PatternStat = {
  pattern: string;
  attempts: number;
  passes: number;
  score: number;
};

export type ProfilePayload = {
  account: { email: string | null; name: string | null; joinedAt: string | null };
  profile: DrillProfile;
  patterns: PatternStat[];
  stats: Stats;
};

export type LeaderboardEntry = {
  rank: number;
  handle: string;
  score: number;
  solved: number;
  attempts: number;
  lastActiveAt: string | null;
};

export const api = {
  me: () => request<{ user: User }>("/api/me"),

  login: (email: string, password: string) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  signup: (input: { email: string; password: string; name?: string; marketingConsent: boolean }) =>
    request<{ user: User }>("/api/auth/signup", {
      method: "POST",
      // The API requires `acceptedTerms` to be literally true, so the signup form
      // has to make accepting them a deliberate act rather than a default.
      body: JSON.stringify({ ...input, acceptedTerms: true }),
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  profile: () =>
    request<ProfilePayload>(`/api/drills/profile?utcOffsetMinutes=${utcOffsetMinutes()}`),

  saveProfile: (input: Partial<DrillProfile>) =>
    request<DrillProfile>("/api/drills/profile", {
      method: "PUT",
      body: JSON.stringify(input),
    }),

  leaderboard: () => request<{ entries: LeaderboardEntry[] }>("/api/drills/leaderboard"),

  history: (limit = 50) => request<{ attempts: Attempt[] }>(`/api/drills/history?limit=${limit}`),

  state: () =>
    request<{ progress: Progress[]; stats: Stats; now: string }>(
      `/api/drills/state?utcOffsetMinutes=${utcOffsetMinutes()}`,
    ),

  attempt: (facts: AttemptFacts) =>
    request<AttemptResult>("/api/drills/attempt", {
      method: "POST",
      body: JSON.stringify({ ...facts, utcOffsetMinutes: utcOffsetMinutes() }),
    }),
};
